import { Button, Drawer, Form, Input, message } from 'antd';
import { useEffect, useState } from 'react';
import customerTypeService from '../../services/customer-type.service';

function AddEditCustomerType({ open, onClose, customerType }) {
  const [form] = Form.useForm();
  const [btnLoading, setBtnLoading] = useState(false);
  const [formErrorMessage, setFormErrorMessage] = useState(null);
  const isEditing = !!customerType;

  useEffect(() => {
    if (open) {
      if (isEditing && customerType) {
        form.setFieldsValue({
          name: customerType.name,
        });
      } else {
        form.resetFields();
        form.setFieldsValue({
          name: '',
        });
      }
      setFormErrorMessage(null);
    }
  }, [open, isEditing, customerType, form]);

  const handleSubmit = async values => {
    setBtnLoading(true);
    setFormErrorMessage(null);

    try {
      let response;
      if (isEditing) {
        response = await customerTypeService.updateCustomerType(
          customerType.id,
          values,
        );
      } else {
        response = await customerTypeService.addCustomerType(values);
      }

      if (response.data) {
        message.success(
          `Customer type ${isEditing ? 'updated' : 'created'} successfully!`,
        );
        onClose();
      }
    } catch (error) {
      setBtnLoading(false);
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // Check for field-specific errors first (name field)
        if (errorData.name) {
          setFormErrorMessage(
            Array.isArray(errorData.name)
              ? errorData.name.join(', ')
              : typeof errorData.name === 'string'
              ? errorData.name
              : 'Invalid customer type name',
          );
        } 
        // Check if message is an object (like {name: ["error"]} or {non_field_errors: [...]})
        else if (errorData.message) {
          // Backend sometimes returns `message` as an object (e.g., { non_field_errors: [...] })
          if (
            typeof errorData.message === 'object' &&
            !Array.isArray(errorData.message) &&
            errorData.message !== null
          ) {
            // Handle message as object (e.g., {name: ["error message"]} or {non_field_errors: [...]})
            if (errorData.message.name) {
              setFormErrorMessage(
                Array.isArray(errorData.message.name)
                  ? errorData.message.name.join(', ')
                  : errorData.message.name,
              );
            } else if (errorData.message.non_field_errors) {
              const nf = errorData.message.non_field_errors;
              message.error(Array.isArray(nf) ? nf.join(', ') : String(nf));
            } else {
              // Extract all error messages from the message object
              const messages = [];
              for (const key in errorData.message) {
                if (Array.isArray(errorData.message[key])) {
                  messages.push(...errorData.message[key]);
                } else if (typeof errorData.message[key] === 'string') {
                  messages.push(errorData.message[key]);
                }
              }
              if (messages.length > 0) {
                setFormErrorMessage(messages.join(', '));
              } else {
                message.error('An error occurred. Please try again.');
              }
            }
          } else if (typeof errorData.message === 'string') {
            // Handle message as string
            message.error(errorData.message);
          } else if (Array.isArray(errorData.message)) {
            // Handle message as array
            message.error(errorData.message.join(', '));
          }
        } else if (errorData.non_field_errors) {
          message.error(
            Array.isArray(errorData.non_field_errors)
              ? errorData.non_field_errors.join(', ')
              : errorData.non_field_errors,
          );
        } else {
          // Fallback: extract all error messages
          const messages = [];
          for (const key in errorData) {
            if (Array.isArray(errorData[key])) {
              messages.push(`${key}: ${errorData[key].join(', ')}`);
            } else if (typeof errorData[key] === 'string') {
              messages.push(`${key}: ${errorData[key]}`);
            }
          }
          if (messages.length > 0) {
            message.error(messages.join('; '));
          } else {
            message.error('An unknown error occurred.');
          }
        }
      } else if (typeof error === 'string') {
        message.error(error);
      } else if (error instanceof Error) {
        message.error(error.message);
      } else {
        message.error('An unknown error occurred.');
      }
    } finally {
      setBtnLoading(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setFormErrorMessage(null);
    onClose();
  };

  return (
    <Drawer
      title={isEditing ? 'Edit Customer Type' : 'Add Customer Type'}
      width={400}
      onClose={handleClose}
      open={open}
      footer={null}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          name: '',
        }}
      >
        <Form.Item
          name="name"
          label="Customer Type"
          style={{ maxWidth: 420 }}
          rules={[
            { required: true, message: 'Please enter a customer type!' },
            {
              min: 2,
              message: 'Customer type must be at least 2 characters long!',
            },
            {
              max: 255,
              message: 'Customer type cannot exceed 255 characters!',
            },
            {
              pattern: /^[a-zA-Z0-9\s\-_'&.]+$/,
              message:
                'Customer type can only contain letters, numbers, spaces, hyphens, underscores, apostrophes, ampersands, and periods!',
            },
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                if (value.trim() === '') {
                  return Promise.reject(
                    new Error('Customer type cannot be empty spaces!'),
                  );
                }
                if (value.trim() !== value) {
                  return Promise.reject(
                    new Error(
                      'Customer type cannot have leading or trailing spaces!',
                    ),
                  );
                }
                if (/\s{2,}/.test(value)) {
                  return Promise.reject(
                    new Error('Customer type cannot have consecutive spaces!'),
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
          help={formErrorMessage || undefined}
          validateStatus={formErrorMessage ? 'error' : undefined}
        >
          <Input
            placeholder="Enter Customer Type"
            style={{ width: '100%' }}
            maxLength={255}
            onChange={e => {
              // Clear error message when user starts typing
              setFormErrorMessage(null);
              const input = e.target;
              const value = input.value;
              const caretPosition = input.selectionStart;
              const formattedValue = value
                .toLowerCase()
                .replace(/\b\w/g, char => char.toUpperCase());
              form.setFieldsValue({ name: formattedValue });
              setTimeout(() => {
                input.setSelectionRange(caretPosition, caretPosition);
              }, 0);
            }}
          />
        </Form.Item>

        <div
          className="flex justify-end"
          style={{ marginTop: 24, gap: 8, alignItems: 'center' }}
        >
          <Button onClick={handleClose} size="middle">
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={btnLoading}
            size="middle"
          >
            {isEditing ? 'Update Customer Type' : 'Create Customer Type'}
          </Button>
        </div>
      </Form>
    </Drawer>
  );
}

export default AddEditCustomerType;
